<!-- Enhanced Error Display -->
<div class="error-toast" *ngIf="errorMessage.length > 0">
  <mat-icon class="error-icon">error_outline</mat-icon>
  <span>{{errorMessage}}</span>
  <button class="close-error" (click)="errorMessage = ''">
    <mat-icon>close</mat-icon>
  </button>
</div>

<div class="background-wrapper">
  <div class="fitness-background">
    <div class="brand-overlay">
      <h1 class="brand-title">NEAT Nutrition and Fitness</h1>
      <p class="brand-tagline">Transform Your Life, One Step at a Time</p>
    </div>
  </div>
  <div class="signup-container">
    
    <!-- Step 1: Email Input -->
    <div class="signup-card-wrapper" *ngIf="currentStep === 1">
      <mat-card class="signup-card">
        <div class="header">
          <div class="logo-section">
            <div class="logo-icon">
              <mat-icon>fitness_center</mat-icon>
            </div>
            <h1 class="card-title">Join NEAT</h1>
            <p class="card-subtitle">Start Your Fitness Journey Today</p>
          </div>
        </div>

        <div class="signup-content">
          <form [formGroup]="emailForm" (ngSubmit)="onEmailSubmit()" class="auth-form">
            <div class="form-header">
              <h2 class="form-title">Create Account</h2>
              <p class="form-description">Enter your email to get started</p>
            </div>
            
            <div class="input-group">
              <mat-form-field class="premium-input" appearance="outline">
                <mat-icon matPrefix class="input-icon">email</mat-icon>
                <input matInput placeholder="Enter your email address" formControlName="email" required autocomplete="off">
                <mat-error *ngIf="isFieldInvalid(emailForm, 'email')">
                  {{getFieldError(emailForm, 'email')}}
                </mat-error>
              </mat-form-field>
            </div>

            <!-- User Type Selection -->
            <div class="user-type-selection">
              <p class="selection-label">I am a:</p>
              <mat-radio-group [(ngModel)]="selectedUserType" (change)="onUserTypeChange($event.value)" class="user-type-group">
                <div *ngFor="let option of userTypeOptions" 
                     class="user-type-card" 
                     [class.selected]="selectedUserType === option.value"
                     [class.disabled]="option.value === 'client'"
                     (click)="onUserTypeChange(option.value)">
                  <mat-radio-button [value]="option.value" 
                                    [disabled]="option.value === 'client'"
                                    class="user-type-radio">
                  </mat-radio-button>
                  <div class="card-content">
                    <div class="icon-container">
                      <mat-icon class="user-type-icon">{{option.icon}}</mat-icon>
                    </div>
                    <div class="text-content">
                      <h3 class="option-title">{{option.label}}</h3>
                      <p class="option-description">{{option.description}}</p>
                    </div>
                  </div>
                </div>
              </mat-radio-group>
            </div>

            <!-- Client Error Message -->
            <div class="client-error-section" *ngIf="showClientError">
              <div class="error-container">
                <mat-icon class="error-icon">info_outline</mat-icon>
                <span class="error-text">
                  Please wait for your trainer's invitation and sign up through the NEAT mobile app. We have an awesome experience awaiting you.
                </span>
              </div>
            </div>

            <button mat-raised-button type="submit" class="primary-btn" [disabled]="isLoading">
              <span *ngIf="!isLoading">Continue</span>
              <span *ngIf="isLoading">Validating...</span>
              <mat-icon class="btn-icon" *ngIf="!isLoading">arrow_forward</mat-icon>
            </button>
          </form>

          <!-- Email exists error -->
          <div class="email-exists-section" *ngIf="emailExists">
            <div class="error-container">
              <mat-icon class="error-icon">error_outline</mat-icon>
              <span class="error-text">This email is already registered.</span>
            </div>
            <div class="action-buttons">
              <button mat-stroked-button (click)="tryDifferentEmail()" class="secondary-btn">
                Try Different Email
              </button>
              <button mat-raised-button (click)="goToLogin()" class="primary-btn">
                Go to Login
              </button>
            </div>
          </div>

          <!-- Login Link -->
          <div class="login-section">
            <div class="login-divider"></div>
            <p class="login-text">
              Already have an account? 
              <button class="login-btn" (click)="goToLogin()">
                <span>Sign in here</span>
                <mat-icon>arrow_forward</mat-icon>
              </button>
            </p>
          </div>
        </div>
      </mat-card>
    </div>

    <!-- Step 2: Complete Signup Form -->
    <div class="signup-card-wrapper" *ngIf="currentStep === 2">
      <mat-card class="signup-card signup-form">
        <div class="header">
          <div class="back-button" (click)="goBackToEmailStep()">
            <mat-icon>arrow_back</mat-icon>
          </div>
          <div class="logo-section">
            <div class="logo-icon">
              <mat-icon>fitness_center</mat-icon>
            </div>
            <h1 class="card-title">Complete Profile</h1>
            <p class="card-subtitle">Finish setting up your account</p>
          </div>
        </div>

        <div class="signup-content">
          <form [formGroup]="signupForm" (ngSubmit)="onSignupSubmit()" class="auth-form">
            
            <div class="input-group">
              <!-- Email -->
              <mat-form-field class="premium-input" appearance="outline">
                <mat-icon matPrefix class="input-icon">email</mat-icon>
                <input matInput placeholder="Email Address" formControlName="email" required autocomplete="off" readonly>
                <mat-error *ngIf="isFieldInvalid(signupForm, 'email')">
                  {{getFieldError(signupForm, 'email')}}
                </mat-error>
              </mat-form-field>

              <!-- Company Name -->
              <mat-form-field class="premium-input" appearance="outline">
                <mat-icon matPrefix class="input-icon">business</mat-icon>
                <input matInput placeholder="Company Name" formControlName="companyName" required autocomplete="off">
                <mat-error *ngIf="isFieldInvalid(signupForm, 'companyName')">
                  {{getFieldError(signupForm, 'companyName')}}
                </mat-error>
              </mat-form-field>

              <!-- First Name and Last Name in a row -->
              <div class="name-row">
                <mat-form-field class="premium-input half-width" appearance="outline">
                  <mat-icon matPrefix class="input-icon">person</mat-icon>
                  <input matInput placeholder="First Name" formControlName="firstName" required autocomplete="off">
                  <mat-error *ngIf="isFieldInvalid(signupForm, 'firstName')">
                    {{getFieldError(signupForm, 'firstName')}}
                  </mat-error>
                </mat-form-field>

                <mat-form-field class="premium-input half-width" appearance="outline">
                  <mat-icon matPrefix class="input-icon">person_outline</mat-icon>
                  <input matInput placeholder="Last Name" formControlName="lastName" required autocomplete="off">
                  <mat-error *ngIf="isFieldInvalid(signupForm, 'lastName')">
                    {{getFieldError(signupForm, 'lastName')}}
                  </mat-error>
                </mat-form-field>
              </div>

              <!-- Password with strength indicator -->
              <mat-form-field class="premium-input" appearance="outline">
                <mat-icon matPrefix class="input-icon">lock_outline</mat-icon>
                <input matInput type="password" placeholder="Create a strong password" formControlName="password" required autocomplete="off">
                <mat-error *ngIf="isFieldInvalid(signupForm, 'password')">
                  {{getFieldError(signupForm, 'password')}}
                </mat-error>
              </mat-form-field>
              
              <!-- Password strength indicator -->
              <div class="password-strength" *ngIf="signupForm.get('password')?.value">
                <div class="strength-bar">
                  <div class="strength-fill" 
                       [style.width.%]="getPasswordStrength() === 'weak' ? 33 : getPasswordStrength() === 'medium' ? 66 : 100"
                       [style.background-color]="getPasswordStrengthColor()">
                  </div>
                </div>
                <span class="strength-text" [style.color]="getPasswordStrengthColor()">
                  Password strength: {{getPasswordStrength()}}
                </span>
              </div>

              <!-- Company URL -->
              <mat-form-field class="premium-input" appearance="outline">
                <mat-icon matPrefix class="input-icon">link</mat-icon>
                <input matInput placeholder="Company URL (e.g., mycompany)" formControlName="companyUrl" required autocomplete="off">
                <mat-hint>This will be used for your custom login URL</mat-hint>
                <mat-error *ngIf="isFieldInvalid(signupForm, 'companyUrl')">
                  {{getFieldError(signupForm, 'companyUrl')}}
                </mat-error>
              </mat-form-field>

              <!-- Number of Employees -->
              <mat-form-field class="premium-input" appearance="outline">
                <mat-icon matPrefix class="input-icon">groups</mat-icon>
                <mat-select placeholder="Number of Employees" formControlName="employeeCount" required>
                  <mat-option *ngFor="let option of employeeCountOptions" [value]="option.value">
                    {{option.label}}
                  </mat-option>
                </mat-select>
                <mat-error *ngIf="isFieldInvalid(signupForm, 'employeeCount')">
                  {{getFieldError(signupForm, 'employeeCount')}}
                </mat-error>
              </mat-form-field>
            </div>

            <button mat-raised-button type="submit" class="primary-btn" [disabled]="isLoading">
              <span *ngIf="!isLoading">Create Account</span>
              <span *ngIf="isLoading">Creating Account...</span>
              <mat-icon class="btn-icon" *ngIf="!isLoading">check_circle</mat-icon>
            </button>
          </form>

          <!-- Login Link -->
          <div class="login-section">
            <div class="login-divider"></div>
            <p class="login-text">
              Already have an account? 
              <button class="login-btn" (click)="goToLogin()">
                <span>Sign in here</span>
                <mat-icon>arrow_forward</mat-icon>
              </button>
            </p>
          </div>
        </div>
      </mat-card>
    </div>

  </div>
</div>